import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { FEATURED_PROJECTS, GRAPHQL_URL, PROJECTS_PER_PAGE } from "config"
import { OrderDirection, Project } from "utils/types"
import FeaturedProjectPreview from "components/FeaturedProjectPreview"
import MarketplaceWorkPreview from "components/MarketplaceWorkPreview"
import ProjectPreview from "components/ProjectPreview"
import Loading from "components/Loading"
import useProjects from "hooks/useProjects"
import useWindowSize from "hooks/useWindowSize"
import useCountProjects from "hooks/useCountProjects"
import { MARKETPLACE_WORKS } from "data/marketplaceWorks"

type WorkFilter = "generative" | "1of1"

const Projects = () => {
  const windowSize = useWindowSize()
  const [searchParams, setSearchParams] = useSearchParams()
  const [countProjects, setCountProjects] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const skip = currentPage * PROJECTS_PER_PAGE
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.DESC)
  const { loading, error, data } = useProjects({ skip, first: PROJECTS_PER_PAGE, orderDirection })
  const countProjectsResponse = useCountProjects()
  const totalProjectCount = countProjectsResponse.data?.projects?.length
  const queriedProjects = data?.projects || []
  const hasGraphqlEndpoint = Boolean(GRAPHQL_URL)
  const hasQueriedProjects = queriedProjects.length > 0
  const queryLoading = hasGraphqlEndpoint && loading
  const queryError = hasGraphqlEndpoint && error

  // Radio-like category filter, persisted in the URL. No param = show all.
  const filterParam = searchParams.get("filter")
  const filter: WorkFilter | null =
    filterParam === "generative" || filterParam === "1of1" ? filterParam : null
  const showGenerative = filter !== "1of1"
  const showOneOfOne = filter !== "generative"

  const toggleFilter = (category: WorkFilter) => {
    const next = filter === category ? null : category
    const params = new URLSearchParams(searchParams)
    if (next) params.set("filter", next)
    else params.delete("filter")
    setCurrentPage(0)
    setSearchParams(params)
  }

  useEffect(() => {
    if (totalProjectCount !== undefined) {
      setCountProjects(totalProjectCount)
    }
  }, [totalProjectCount])

  const artBlocksProjectCount = totalProjectCount ?? countProjects
  const containerWidth = Math.min(windowSize.width, 1200)
  const horizontalPadding = windowSize.width >= 768 ? 64 : windowSize.width >= 640 ? 48 : 40
  const columns = windowSize.width >= 768 ? 3 : windowSize.width >= 640 ? 2 : 1
  const gridGap = windowSize.width >= 768 ? 24 : 20
  const colWidth = Math.max(
    160,
    (containerWidth - horizontalPadding - gridGap * (columns - 1)) / columns - 32
  )

  const countLoading = hasGraphqlEndpoint && countProjectsResponse.loading && totalProjectCount === undefined
  const generativeSeriesCount = artBlocksProjectCount + FEATURED_PROJECTS.length
  const oneOfOneWorksCount = MARKETPLACE_WORKS.length
  const totalPages = Math.max(1, Math.ceil(artBlocksProjectCount / PROJECTS_PER_PAGE))

  const featuredCards =
    currentPage === 0 && showGenerative
      ? FEATURED_PROJECTS.map((project) => (
          <FeaturedProjectPreview key={project.key} project={project} width={Math.floor(colWidth)} />
        ))
      : []
  const oneOfOneCards =
    currentPage === 0 && showOneOfOne
      ? MARKETPLACE_WORKS.map((work) => <MarketplaceWorkPreview key={work.id} work={work} />)
      : []
  const queriedCards = showGenerative
    ? queriedProjects.map((project: Project) => (
        <ProjectPreview
          key={project.id}
          project={project}
          width={Math.floor(colWidth)}
          showDescription
          statusLabel="Sold Out"
          soldOutEdition
        />
      ))
    : []
  const spotlight = [...featuredCards, ...oneOfOneCards]
  const gridCards =
    orderDirection === OrderDirection.DESC ? [...spotlight, ...queriedCards] : [...queriedCards, ...spotlight]
  const soldInventoryLabel =
    showGenerative && showOneOfOne
      ? `${countLoading ? "..." : generativeSeriesCount} series / ${oneOfOneWorksCount} 1/1 works`
      : showGenerative
        ? `${countLoading ? "..." : generativeSeriesCount} series`
        : `${oneOfOneWorksCount} 1/1 works`

  return (
    <div>
      <div className="gallery-page-head">
        <div className="max-w-3xl">
          <h1 className="gallery-heading">Work</h1>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-gallery-muted">
            Generative art projects and 1/1 works by r4v3n, including Dots, Dots: Composites,
            Art Blocks collections, SuperRare 1/1s, and Foundation 1/1s.
          </p>
          <div className="mt-7 grid grid-cols-2 gap-3 max-w-md" role="group" aria-label="Filter works by category">
            <button
              type="button"
              onClick={() => toggleFilter("generative")}
              aria-pressed={filter === "generative"}
              className={`gallery-stat gallery-stat-toggle ${filter === "generative" ? "is-active" : ""}`}
            >
              <span>{countLoading ? "..." : generativeSeriesCount}</span>
              <p>Generative series</p>
            </button>
            <button
              type="button"
              onClick={() => toggleFilter("1of1")}
              aria-pressed={filter === "1of1"}
              className={`gallery-stat gallery-stat-toggle ${filter === "1of1" ? "is-active" : ""}`}
            >
              <span>{oneOfOneWorksCount}</span>
              <p>1/1 works</p>
            </button>
          </div>
        </div>

        {!queryLoading && !queryError && hasQueriedProjects && (
          <label className="gallery-select-wrap">
            <span>Sort</span>
            <select
              aria-label="Sort projects"
              value={orderDirection}
              onChange={(e) => {
                setCurrentPage(0)
                setOrderDirection(e.target.value as OrderDirection)
              }}
              className="gallery-select"
            >
              <option value={OrderDirection.DESC}>Newest</option>
              <option value={OrderDirection.ASC}>Oldest</option>
            </select>
          </label>
        )}
      </div>

      {FEATURED_PROJECTS.length > 0 || MARKETPLACE_WORKS.length > 0 || hasQueriedProjects ? (
        <>
          <section className="mb-12 md:mb-16" aria-labelledby="available-work-heading">
            <div className="mb-5 flex items-end justify-between gap-4 border-b border-gallery-border pb-4">
              <h2 id="available-work-heading" className="font-serif text-3xl leading-tight text-gallery-text md:text-4xl">
                Available
              </h2>
              <span className="gallery-label text-gallery-muted">0 available</span>
            </div>
            <div className="gallery-empty">Nothing available right now.</div>
          </section>

          <section aria-labelledby="sold-work-heading">
            <div className="mb-5 flex items-end justify-between gap-4 border-b border-gallery-border pb-4">
              <h2 id="sold-work-heading" className="font-serif text-3xl leading-tight text-gallery-text md:text-4xl">
                Sold
              </h2>
              <span className="gallery-label text-gallery-muted">
                {soldInventoryLabel}
              </span>
            </div>

            <div
              key={filter ?? "all"}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 items-start gap-5 md:gap-6 [animation:gallery-fade-in_0.3s_ease]"
            >
              {gridCards}
            </div>
          </section>

          {queryLoading && showGenerative && <Loading />}
          {queryError && showGenerative && (
            <div className="gallery-empty text-red-500 mt-8">Error loading Art Blocks projects.</div>
          )}

          {showGenerative && totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-16 mb-8">
              <button
                aria-label="Previous projects page"
                disabled={currentPage === 0}
                onClick={() => { window.scrollTo(0, 0); setCurrentPage(currentPage - 1) }}
                className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
              >
                ← Prev
              </button>
              <span className="gallery-label text-gallery-muted">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                aria-label="Next projects page"
                disabled={currentPage === totalPages - 1}
                onClick={() => { window.scrollTo(0, 0); setCurrentPage(currentPage + 1) }}
                className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </>
      ) : queryLoading ? (
        <Loading />
      ) : (
        <div className="gallery-empty">No projects found for the configured r4v3n artist addresses.</div>
      )}
    </div>
  )
}

export default Projects
